import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Upload, Trash2, Save, ArrowLeft } from "lucide-react";
import api from "@/lib/api";

const inputCls =
  "w-full bg-storm-800 border border-storm-600 rounded-md px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-gold";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5";

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onSuccess(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-storm flex items-center justify-center px-4 hero-grid" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-gold/20 bg-storm-800/60 backdrop-blur p-8" data-testid="admin-login-form">
        <img src="/logo.png" alt="DRC502" className="h-14 w-auto mx-auto mb-6" />
        <h1 className="font-display font-bold uppercase italic text-2xl text-white text-center mb-6">Admin Portal</h1>
        {error && <p className="mb-4 text-sm text-red-400 text-center" data-testid="admin-login-error">{error}</p>}
        <label className={labelCls} htmlFor="a-email">Email</label>
        <input id="a-email" type="email" required className={inputCls} data-testid="admin-email-input"
          value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@drc502.com" />
        <label className={`${labelCls} mt-4`} htmlFor="a-pass">Password</label>
        <input id="a-pass" type="password" required className={inputCls} data-testid="admin-password-input"
          value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
        <button type="submit" disabled={loading} data-testid="admin-login-submit"
          className="mt-6 w-full bg-gold text-storm font-bold py-3 rounded-full hover:bg-gold-light transition-colors disabled:opacity-60">
          {loading ? "Signing in…" : "Sign In"}
        </button>
        <Link to="/" data-testid="admin-back-link" className="mt-4 flex items-center justify-center gap-1 text-xs text-slate-500 hover:text-gold transition-colors">
          <ArrowLeft size={13} /> Back to website
        </Link>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, textarea, testId }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {textarea ? (
        <textarea rows={3} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
      ) : (
        <input className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
      )}
    </div>
  );
}

function ContentTab() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setC(r.data)).catch(() => toast.error("Could not load content"));
  }, []);

  if (!c) return <p className="text-slate-500 text-sm">Loading…</p>;

  const set = (k, v) => setC({ ...c, [k]: v });
  const setArr = (key, i, field, v) => {
    const arr = [...(c[key] || [])];
    arr[i] = { ...arr[i], [field]: v };
    setC({ ...c, [key]: arr });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", c);
      toast.success("Content saved — live on the site now.");
    } catch {
      toast.error("Save failed. Are you still signed in?");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-content-tab">
      <section className="grid sm:grid-cols-2 gap-4">
        <Field label="Company name" value={c.company_name} onChange={(v) => set("company_name", v)} testId="edit-company-name" />
        <Field label="Tagline" value={c.tagline} onChange={(v) => set("tagline", v)} testId="edit-tagline" />
        <Field label="Phone (display)" value={c.phone_display} onChange={(v) => set("phone_display", v)} testId="edit-phone-display" />
        <Field label="Phone (tel link)" value={c.phone_tel} onChange={(v) => set("phone_tel", v)} testId="edit-phone-tel" />
        <Field label="WhatsApp number (intl, no +)" value={c.whatsapp} onChange={(v) => set("whatsapp", v)} testId="edit-whatsapp" />
        <Field label="Public email" value={c.email} onChange={(v) => set("email", v)} testId="edit-email" />
        <div className="sm:col-span-2">
          <Field label="Notification email — quote requests are sent here" value={c.notification_email} onChange={(v) => set("notification_email", v)} testId="edit-notification-email" />
        </div>
      </section>

      <section className="grid sm:grid-cols-2 gap-4">
        <h3 className="sm:col-span-2 font-display font-bold uppercase italic text-xl text-gold">Hero</h3>
        <Field label="Badge" value={c.hero_badge} onChange={(v) => set("hero_badge", v)} testId="edit-hero-badge" />
        <Field label="Headline line 1" value={c.hero_line_1} onChange={(v) => set("hero_line_1", v)} testId="edit-hero-line-1" />
        <Field label="Headline line 2 (gold)" value={c.hero_line_2} onChange={(v) => set("hero_line_2", v)} testId="edit-hero-line-2" />
        <Field label="Headline line 3" value={c.hero_line_3} onChange={(v) => set("hero_line_3", v)} testId="edit-hero-line-3" />
        <div className="sm:col-span-2">
          <Field label="Subtitle" textarea value={c.hero_subtitle} onChange={(v) => set("hero_subtitle", v)} testId="edit-hero-subtitle" />
        </div>
      </section>

      <section className="grid sm:grid-cols-2 gap-4">
        <h3 className="sm:col-span-2 font-display font-bold uppercase italic text-xl text-gold">Stats</h3>
        {(c.stats || []).map((s, i) => (
          <div key={i} className="grid grid-cols-2 gap-2">
            <Field label={`Stat ${i + 1} value`} value={s.value} onChange={(v) => setArr("stats", i, "value", v)} testId={`edit-stat-${i}-value`} />
            <Field label={`Stat ${i + 1} label`} value={s.label} onChange={(v) => setArr("stats", i, "label", v)} testId={`edit-stat-${i}-label`} />
          </div>
        ))}
      </section>

      <section className="space-y-4">
        <h3 className="font-display font-bold uppercase italic text-xl text-gold">Services</h3>
        {(c.services || []).map((s, i) => (
          <div key={i} className="rounded-xl border border-storm-600 p-4 grid sm:grid-cols-2 gap-3" data-testid={`edit-service-${i}`}>
            <Field label="Title" value={s.title} onChange={(v) => setArr("services", i, "title", v)} testId={`edit-service-${i}-title`} />
            <Field label="Tags (comma separated)" value={s.tags} onChange={(v) => setArr("services", i, "tags", v)} testId={`edit-service-${i}-tags`} />
            <div className="sm:col-span-2">
              <Field label="Description" textarea value={s.desc} onChange={(v) => setArr("services", i, "desc", v)} testId={`edit-service-${i}-desc`} />
            </div>
          </div>
        ))}
      </section>

      <section className="grid sm:grid-cols-2 gap-4">
        <h3 className="sm:col-span-2 font-display font-bold uppercase italic text-xl text-gold">Sections</h3>
        <Field label="Storm banner title" value={c.storm_title} onChange={(v) => set("storm_title", v)} testId="edit-storm-title" />
        <Field label="Storm banner text" value={c.storm_text} onChange={(v) => set("storm_text", v)} testId="edit-storm-text" />
        <Field label="About title" value={c.about_title} onChange={(v) => set("about_title", v)} testId="edit-about-title" />
        <Field label="Service area" value={c.area_text} onChange={(v) => set("area_text", v)} testId="edit-area-text" />
        <div className="sm:col-span-2">
          <Field label="About text" textarea value={c.about_text} onChange={(v) => set("about_text", v)} testId="edit-about-text" />
        </div>
        <div className="sm:col-span-2">
          <Field label="Marquee items (comma separated)" textarea value={c.marquee} onChange={(v) => set("marquee", v)} testId="edit-marquee" />
        </div>
      </section>

      <section className="grid sm:grid-cols-1 gap-4">
        <h3 className="font-display font-bold uppercase italic text-xl text-gold">SEO</h3>
        <Field label="SEO title" value={c.seo_title} onChange={(v) => set("seo_title", v)} testId="edit-seo-title" />
        <Field label="SEO description" textarea value={c.seo_description} onChange={(v) => set("seo_description", v)} testId="edit-seo-description" />
      </section>

      <button onClick={save} disabled={saving} data-testid="admin-save-content"
        className="sticky bottom-4 inline-flex items-center gap-2 bg-gold text-storm font-bold px-8 py-3.5 rounded-full hover:bg-gold-light transition-colors disabled:opacity-60 shadow-lg shadow-gold/20">
        <Save size={16} /> {saving ? "Saving…" : "Save All Changes"}
      </button>
    </div>
  );
}

function GalleryTab() {
  const [images, setImages] = useState([]);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Roofing");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/images").then((r) => setImages(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose an image first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("category", category);
      await api.post("/admin/images", fd);
      toast.success("Image published to the gallery");
      setFile(null); setTitle("");
      document.getElementById("g-file").value = "";
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/images/${id}`);
      toast.success("Image removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-gallery-tab">
      <form onSubmit={upload} className="rounded-xl border border-storm-600 p-5 grid sm:grid-cols-4 gap-3 items-end mb-8" data-testid="gallery-upload-form">
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="g-file">Image file (max 10MB)</label>
          <input id="g-file" type="file" accept="image/*" data-testid="gallery-file-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full text-sm text-slate-400 file:mr-3 file:rounded-full file:border-0 file:bg-gold file:px-4 file:py-2 file:text-sm file:font-semibold file:text-storm hover:file:bg-gold-light" />
        </div>
        <div>
          <label className={labelCls}>Title</label>
          <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="New roof in Toms River" data-testid="gallery-title-input" />
        </div>
        <div>
          <label className={labelCls}>Category</label>
          <select className={inputCls} value={category} onChange={(e) => setCategory(e.target.value)} data-testid="gallery-category-select">
            {["Roofing", "Siding", "Gutters", "Storm Damage", "Exterior Restoration"].map((o) => <option key={o}>{o}</option>)}
          </select>
        </div>
        <button type="submit" disabled={busy} data-testid="gallery-upload-button"
          className="sm:col-span-4 inline-flex items-center justify-center gap-2 bg-gold text-storm font-bold py-3 rounded-full hover:bg-gold-light transition-colors disabled:opacity-60">
          <Upload size={16} /> {busy ? "Uploading…" : "Add to Gallery"}
        </button>
      </form>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {images.map((img) => (
          <div key={img.id} className="group relative rounded-xl overflow-hidden border border-storm-600 aspect-[4/3]" data-testid={`admin-image-${img.id}`}>
            <img src={img.url} alt={img.title} className="w-full h-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 bg-storm/90 p-2.5 flex items-center justify-between gap-2">
              <span className="text-xs text-slate-300 truncate">{img.title}</span>
              <button onClick={() => remove(img.id)} data-testid={`admin-image-delete-${img.id}`}
                className="p-1.5 rounded-full text-red-400 hover:bg-red-500/15 transition-colors shrink-0" aria-label="Delete image">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
      {images.length === 0 && <p className="text-slate-500 text-sm">No images yet.</p>}
    </div>
  );
}

function LeadsTab() {
  const [leads, setLeads] = useState(null);
  useEffect(() => {
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => setLeads([]));
  }, []);

  if (!leads) return <p className="text-slate-500 text-sm">Loading…</p>;
  if (leads.length === 0) return <p className="text-slate-500 text-sm" data-testid="leads-empty">No quote requests yet. They'll appear here as soon as someone submits the form.</p>;

  return (
    <div className="overflow-x-auto" data-testid="admin-leads-tab">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-storm-600">
            <th className="py-3 pr-4">Date</th><th className="py-3 pr-4">Name</th><th className="py-3 pr-4">Phone</th>
            <th className="py-3 pr-4">Email</th><th className="py-3 pr-4">Service</th><th className="py-3">Message</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((l) => (
            <tr key={l.id} className="border-b border-storm-600/50 text-slate-300" data-testid={`lead-row-${l.id}`}>
              <td className="py-3 pr-4 whitespace-nowrap text-slate-500">{new Date(l.created_at).toLocaleString()}</td>
              <td className="py-3 pr-4 font-semibold text-white">{l.name}</td>
              <td className="py-3 pr-4"><a href={`tel:${l.phone}`} className="text-gold hover:underline">{l.phone}</a></td>
              <td className="py-3 pr-4">{l.email || "—"}</td>
              <td className="py-3 pr-4">{l.service || "—"}</td>
              <td className="py-3 max-w-xs truncate" title={l.message}>{l.message || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("content");

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  if (user === null) return <div className="min-h-screen bg-storm" />;
  if (!user) return <Login onSuccess={setUser} />;

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  const tabs = [["content", "Site Content"], ["gallery", "Gallery"], ["leads", "Quote Requests"]];

  return (
    <div className="min-h-screen bg-storm text-foreground" data-testid="admin-dashboard">
      <header className="border-b border-gold/15 bg-storm-800/50 backdrop-blur sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="DRC502" className="h-9 w-auto" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold hidden sm:inline">Admin Portal</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="admin-view-site" className="text-xs text-slate-400 hover:text-gold transition-colors">View site</Link>
            <span className="text-xs text-slate-500 hidden sm:inline" data-testid="admin-user-email">{user.email}</span>
            <button onClick={logout} data-testid="admin-logout-button"
              className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-red-400 transition-colors">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex gap-1 pb-px">
          {tabs.map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}
              className={`px-4 py-2.5 text-sm font-semibold rounded-t-lg transition-colors ${
                tab === key ? "text-gold border-b-2 border-gold" : "text-slate-500 hover:text-slate-300"
              }`}>
              {label}
            </button>
          ))}
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {tab === "content" && <ContentTab />}
        {tab === "gallery" && <GalleryTab />}
        {tab === "leads" && <LeadsTab />}
      </main>
    </div>
  );
}
